/**
 * ModuleRoute — blocks a module route when my_module_permissions() says 'none',
 * redirecting to the first allowed module. When level is 'view', children render
 * in read-only mode (see ModuleLevelContext / useCanEdit).
 */
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { ReadOnlyScope } from '@/shared/ui/ReadOnlyScope';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useModulePermissions } from '@/apps/admin/hooks/useModulePermissions';
import { ADMIN_NAV_CONFIG } from '@/shared/config/platform-config';
import { portalPath } from '@/shared/app/portalPath';
import type { PermLevel } from '@/apps/admin/lib/module-permissions-api';

const ModuleLevelContext = createContext<PermLevel>('edit');
/** True when the current module allows editing. */
export const useCanEdit = () => useContext(ModuleLevelContext) === 'edit';

const PATH_MODULES: [RegExp, string][] = [
  [/^\/dashboard/, 'dashboard'],
  [/^\/lead-submissions/, 'lead_submissions'],
  [/^\/quotes/, 'quotes'],
  [/^\/leads/, 'leads'],
  [/^\/guests/, 'guests'],
  [/^\/(operations|trip-case|transport-booking)/, 'operations_center'],
  [/^\/vendors/, 'vendors'],
  // The legacy URL now opens Packages and follows its access level.
  [/^\/coordination-fees/, 'packages'],
  [/^\/packages/, 'packages'],
  [/^\/pricing-seasons/, 'pricing_seasons'],
  [/^\/settings/, 'settings'],
];
/** Maps an admin URL (with or without /admin prefix) to its module key. */
export function moduleForPath(pathname: string): string | null {
  const path = pathname.replace(/^\/admin(?=\/|$)/, '') || '/';
  return PATH_MODULES.find(([re]) => re.test(path))?.[1] ?? null;
}

export default function ModuleRoute({ module, children }: { module: string; children: ReactNode }) {
  const { isLoading, levelOf, canAccess } = useModulePermissions();
  const p = portalPath('admin');
  const level = levelOf(module);
  const blocked = !isLoading && level === 'none';

  useEffect(() => { if (blocked) toast.error("You don't have access to this module"); }, [blocked]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (blocked) {
    const first = ADMIN_NAV_CONFIG.find(i => i.moduleKey && i.moduleKey !== module && canAccess(i.moduleKey, 'view'));
    const target = first ? p(first.path.replace(/^\/admin(?=\/|$)/, '')) : p('/login');
    return <Navigate to={target} replace />;
  }
  return (
    <ModuleLevelContext.Provider value={level}>
      {level === 'view' ? <ReadOnlyScope>{children}</ReadOnlyScope> : children}
    </ModuleLevelContext.Provider>
  );
}
